import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from './config';
import { useSessionResponses } from './SessionResponsesAPI';

export const sessionsApi = {
    getSessionsByUserId: async (userId, options = {}) => {
        const response = await axiosInstance.get(`/sessions/user/${userId}`, {
            params: {
                ...options
            }
        });
        return response.data;
    },

    getSessionById: async (sessionId, options = {}) => {
        const response = await axiosInstance.get(`/sessions/${sessionId}`, {
            params: {
                ...options
            }
        });
        return response.data;
    },

    getSessionsByIds: async (sessionIds) => {
        // Using query params to send array of IDs
        const response = await axiosInstance.get('/sessions/batch', {
            params: { ids: sessionIds.join(',') }
        });
        return response.data;
    },
    
    updateSessionStatus: async (sessionId, status) => {
        const response = await axiosInstance.put(`/sessions/${sessionId}/status`, {
            status
        });
        return response.data;
    },
    
    createSession: async (userId, name = null) => {
        const response = await axiosInstance.post('/sessions', { userId, name });
        return response.data;
    },

    updateSessionName: async (sessionId, name) => {
        const response = await axiosInstance.put(`/sessions/${sessionId}/name`, { name });
        return response.data;
    }
};

export const useSessionsByUserId = (userId, options = {}) => {
    return useQuery({
        queryKey: ['sessions', 'user', userId, options],
        queryFn: async () => {
            if (!userId) return Promise.resolve([]);
            const result = await sessionsApi.getSessionsByUserId(userId, options);
            return result;
        },
        enabled: !!userId,
    });
};

export const useSessionById = (sessionId, options = {}) => {
    return useQuery({
        queryKey: ['session', sessionId, options],
        queryFn: () => sessionId ? sessionsApi.getSessionById(sessionId, options) : Promise.resolve(null),
        enabled: !!sessionId,
    });
};

export const useSessionsByIds = (sessionIds) => {
    return useQuery({
        queryKey: ['sessions', 'ids', sessionIds],
        queryFn: () => sessionIds?.length > 0 
            ? sessionsApi.getSessionsByIds(sessionIds) 
            : Promise.resolve([]),
        enabled: !!sessionIds?.length,
    });
};

export const useUpdateSessionStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ sessionId, status }) => 
            sessionsApi.updateSessionStatus(sessionId, status),
        onSuccess: (updatedSession) => {
            queryClient.invalidateQueries({ 
                queryKey: ['session', updatedSession.id],
                exact: false 
            });
            queryClient.invalidateQueries({ 
                queryKey: ['sessions'], 
                exact: false 
            });
        }
    });
};

export const useCreateSession = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (payload) => {
            if (typeof payload === 'object') {
                return sessionsApi.createSession(payload.userId, payload.name);
            }
            return sessionsApi.createSession(payload);
        },
        onSuccess: (newSession) => {
            queryClient.invalidateQueries({
                queryKey: ['sessions'],
                exact: false
            });
        }
    });
};

export const useUpdateSessionName = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ sessionId, name }) => sessionsApi.updateSessionName(sessionId, name),
        onSuccess: (updatedSession) => {
            queryClient.invalidateQueries({
                queryKey: ['session', updatedSession.id],
                exact: false
            });
            queryClient.invalidateQueries({
                queryKey: ['sessions'],
                exact: false
            });
            if (updatedSession.cohort_id) {
                queryClient.invalidateQueries({
                    queryKey: ['cohort', updatedSession.cohort_id, 'sessions'],
                    exact: true
                });
            }
        }
    });
};

// Add a convenience hook for completed sessions with rubric count
export const useCompletedSessionsByUserId = (userId) => {
    return useSessionsByUserId(userId, {
        status: 'completed',
        includeRubricCount: true
    });
};

export const useSessionWithCompetencies = (sessionId) => {
  const { data: session } = useSessionById(sessionId, { includeRubricCount: true });
  const { data: responses } = useSessionResponses(sessionId);
  
  return useQuery({
    queryKey: ['session', sessionId, 'withCompetencies'],
    queryFn: () => {
      if (!session || !responses) return null;
      
      return {
        ...session,
        selectedCompetencies: responses,
        generationsRemaining: session.rubric_count ? 3 - session.rubric_count : 3
      };
    },
    enabled: !!sessionId && !!session && !!responses
  });
};

